import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Award, Users, Target, Eye, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { firmInfo, stats, images, teamMembers } from '../data/mock';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: firmInfo.mission
    },
    {
      icon: Eye,
      title: 'Our Vision',
      description: firmInfo.vision
    }
  ];

  const whyChooseUs = [
    'Team of qualified and experienced Chartered Accountants',
    'Personalized attention to each client',
    'Transparent and competitive pricing',
    'Quick turnaround time',
    'Up-to-date with latest regulations and compliances',
    'Strong track record of client satisfaction'
  ];

  const teamValues = [
    { icon: Award, title: 'Excellence', description: 'We strive for excellence in every service we provide' },
    { icon: Users, title: 'Client Focus', description: 'Your success is our priority' },
    { icon: CheckCircle, title: 'Integrity', description: 'We uphold the highest ethical standards' }
  ];

  return (
    <div>
      {/* Page Header */}
      <section className="bg-gradient-to-br from-emerald-800 to-emerald-900 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">About Us</h1>
            <p className="text-emerald-200 text-lg max-w-2xl mx-auto">
              Learn more about our firm, our values, and our commitment to your success
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 text-sm">
              <Link to="/" className="text-emerald-300 hover:text-white transition-colors">Home</Link>
              <span className="text-emerald-500">/</span>
              <span className="text-white">About Us</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://raw.githubusercontent.com/riya1234567/peeyush-assets/main/company photo.jpeg" 
                alt="Our Professional Team" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-emerald-600 text-white p-6 rounded-2xl shadow-xl hidden md:block">
                <div className="text-4xl font-bold">{firmInfo.experience}</div>
                <div className="text-emerald-200">Years of Excellence</div>
              </div>
            </div>
            
            <div>
              <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">About {firmInfo.name}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6">
                Your Trusted Chartered Accountants in Agra
              </h2>
              <p className="text-lg text-amber-700 font-medium mb-4 italic">
                "{firmInfo.taglineHindi}"
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {firmInfo.name} is a Chartered Accountancy firm headquartered in Agra, founded in {firmInfo.foundedYear}. We have been catering to clients across sectors, offering a comprehensive range of services tailored to meet the evolving financial and regulatory landscape.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                With over a decade of experience, our team is equipped with technical expertise and practical insights to deliver effective solutions, whether it be for individuals, startups, corporates, or NGOs.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-4 bg-emerald-50 rounded-xl">
                    <div className="text-2xl md:text-3xl font-bold text-emerald-700">{stat.value}</div>
                    <div className="text-gray-600 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Our Team</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">Meet Our Experts</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our team of dedicated professionals brings years of experience and expertise to serve you better.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member) => (
              <Card key={member.id} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden">
                <CardContent className="p-0">
                  <div className="bg-gradient-to-br from-emerald-600 to-emerald-800 p-8 text-center">
                    <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-white/30 shadow-lg">
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-xl font-bold text-white">{member.name}</h3>
                    <p className="text-emerald-200 font-medium">{member.designation}</p>
                    <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-white text-sm">
                      {member.qualification}
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 leading-relaxed">{member.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((item, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                    <item.icon size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-lg">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-emerald-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-300 font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-8">
                Your Trusted Partner for Financial Success
              </h2>
              
              <div className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={18} className="text-white" />
                    </div>
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-6">
              {teamValues.map((value, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-xl">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
                      <value.icon size={24} />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900 mb-2">{value.title}</h4>
                      <p className="text-gray-600">{value.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Our Offices</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Visit Us</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Head Office (H.O.)</h3>
                <p className="text-gray-600">{firmInfo.headOffice}</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Branch Office (B.O.)</h3>
                <p className="text-gray-600">{firmInfo.branchOffice}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              Let's discuss how we can help your business grow with our comprehensive financial services.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white px-8">
                Get in Touch
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
